import { SINGLETONS, clamp } from './editing'
import { CALL_ONLY, CALLED_KINDS, hasCalled } from './calledScreen'
import { placeOnScreen } from './pages'
import { onCall, pageIds, type LayoutDocument, type LayoutElement, type Screen } from './types'

/** Several elements at once: moved, removed, aligned, distributed, copied. */

export type Alignment = 'left' | 'centre' | 'right' | 'top' | 'middle' | 'bottom'

/** Shift-click: the id added to the selection, or taken out of it. */
export function toggleIn(selection: readonly string[], id: string): string[] {
  return selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id]
}

function bounds(els: readonly LayoutElement[]) {
  const left = Math.min(...els.map((e) => e.x))
  const top = Math.min(...els.map((e) => e.y))
  const right = Math.max(...els.map((e) => e.x + e.w))
  const bottom = Math.max(...els.map((e) => e.y + e.h))
  return { left, top, right, bottom }
}

function chosen(doc: LayoutDocument, ids: readonly string[]) {
  return doc.elements.filter((e) => ids.includes(e.id))
}

/** Moves the chosen elements by dx, dy together, stopped as a group at the canvas edge. */
export function moveMany(doc: LayoutDocument, ids: readonly string[], dx: number, dy: number): LayoutDocument {
  const els = chosen(doc, ids)
  if (els.length === 0) return doc
  const b = bounds(els)
  const x = Math.round(Math.max(-b.left, Math.min(dx, doc.canvas.width - b.right)))
  const y = Math.round(Math.max(-b.top, Math.min(dy, doc.canvas.height - b.bottom)))
  return { ...doc, elements: doc.elements.map((e) => (ids.includes(e.id) ? { ...e, x: e.x + x, y: e.y + y } : e)) }
}

export function removeMany(doc: LayoutDocument, ids: readonly string[]): LayoutDocument {
  return { ...doc, elements: doc.elements.filter((e) => !ids.includes(e.id)) }
}

/** Lines the chosen elements up on an edge or centre of the box around them all. */
export function align(doc: LayoutDocument, ids: readonly string[], how: Alignment): LayoutDocument {
  const els = chosen(doc, ids)
  if (els.length < 2) return doc
  const b = bounds(els)
  const place = (e: LayoutElement): LayoutElement => {
    switch (how) {
      case 'left':
        return { ...e, x: b.left }
      case 'right':
        return { ...e, x: b.right - e.w }
      case 'centre':
        return { ...e, x: Math.round((b.left + b.right) / 2 - e.w / 2) }
      case 'top':
        return { ...e, y: b.top }
      case 'bottom':
        return { ...e, y: b.bottom - e.h }
      case 'middle':
        return { ...e, y: Math.round((b.top + b.bottom) / 2 - e.h / 2) }
    }
  }
  return { ...doc, elements: doc.elements.map((e) => (ids.includes(e.id) ? place(e) : e)) }
}

/** Evens the gaps between three or more elements along an axis; the outermost two stay. */
export function distribute(doc: LayoutDocument, ids: readonly string[], axis: 'x' | 'y'): LayoutDocument {
  const els = chosen(doc, ids)
  if (els.length < 3) return doc
  const size = axis === 'x' ? 'w' : 'h'
  const sorted = [...els].sort((a, b) => a[axis] - b[axis] || a.id.localeCompare(b.id))
  const first = sorted[0]!
  const last = sorted[sorted.length - 1]!
  const span = last[axis] + last[size] - first[axis]
  const gap = (span - sorted.reduce((n, e) => n + e[size], 0)) / (sorted.length - 1)
  const at = new Map<string, number>()
  let pos = first[axis]
  for (const e of sorted) {
    at.set(e.id, Math.round(pos))
    pos += e[size] + gap
  }
  return { ...doc, elements: doc.elements.map((e) => (at.has(e.id) ? { ...e, [axis]: at.get(e.id)! } : e)) }
}

/**
 * Copies of elements for this design: new ids, moved by offset as one group that
 * stops at the canvas edge (so their spacing holds), drawn above everything. A
 * kind the design may hold only once and already holds, a service grid, and what only
 * a Called screen shows on a main display without one, are left out.
 */
export function copies(doc: LayoutDocument, from: readonly LayoutElement[], offset: number): LayoutElement[] {
  const taken = new Set(doc.elements.map((e) => e.id))
  const have = new Set(doc.elements.map((e) => e.kind))
  let z = Math.max(0, ...doc.elements.map((e) => e.z ?? 0))
  const kept = from.filter((e) => {
    if (SINGLETONS.includes(e.kind) && have.has(e.kind)) return false
    // A service grid is no longer added, by copy either; old ones stay.
    if (e.kind === 'services') return false
    if (doc.kind === 'display' && !hasCalled(doc) && (onCall(e) || CALL_ONLY.includes(e.kind))) return false
    have.add(e.kind)
    return true
  })
  if (kept.length === 0) return []
  const b = bounds(kept)
  const dx = Math.max(-b.left, Math.min(offset, doc.canvas.width - b.right))
  const dy = Math.max(-b.top, Math.min(offset, doc.canvas.height - b.bottom))
  return kept.map((e) => {
    let id = ''
    do id = `${e.kind}-${Math.random().toString(36).slice(2, 8)}`
    while (taken.has(id))
    taken.add(id)
    z += 10
    // Clamped one by one only for a copy bigger than this canvas.
    return clamp({ ...structuredClone(e), id, z, x: e.x + Math.max(0, dx), y: e.y + Math.max(0, dy) }, doc.canvas)
  })
}

/**
 * Where a pasted element goes on a kiosk: where it was shown, unless a page it was
 * on is not in this design, and then on the screen and page being drawn.
 */
export function forPaste(el: LayoutElement, doc: LayoutDocument, screen: Screen | undefined, page: string | undefined): LayoutElement {
  if (doc.kind === 'display') return pasteOnDisplay(el, doc, screen)
  if (doc.kind !== 'kiosk') return el
  const have = pageIds(doc)
  if (!el.pages || el.pages.every((p) => have.includes(p))) return el
  return placeOnScreen(el, doc, screen, page)
}

/** On a main display: on the Called screen when it is there and may hold the kind, else
 *  on the board; another kind's screens and pages are dropped. */
function pasteOnDisplay(el: LayoutElement, doc: LayoutDocument, screen: Screen | undefined): LayoutElement {
  const call = hasCalled(doc) && (screen === 'call' ? CALLED_KINDS : CALL_ONLY).includes(el.kind)
  if (!call && !el.screens && !el.pages) return el
  const { screens: _screens, pages: _pages, ...plain } = el
  return call ? { ...plain, screens: ['call'] } : plain
}
